import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import {
  $createNodeSelection,
  $getNearestNodeFromDOMNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  COMMAND_PRIORITY_NORMAL,
  DRAGOVER_COMMAND,
  DROP_COMMAND,
  type LexicalEditor,
  type NodeKey,
  PASTE_COMMAND,
} from "lexical";
import { type FC, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { useToken } from "../../../../server/auth/use-token";
import { ImagesService } from "../../../../server/cloudflare/images-service";
import { convertSvgImage } from "../../../../utils/convert-svg-image";
import {
  getImageDimensions,
  getRelativeMaxDimensionsLocal,
} from "../../../../utils/image-dimensions";
import { serviceURL } from "../../../../utils/service-url";
import { uuidv4 } from "../../../../utils/uuid";
import { type ChannelName, isChatChannel, isEmailChannel } from "../../../channels/types";
import { useClaims } from "../../../generic/hooks/use-claims";
import { imageSizeLimit } from "../../../lexical-chat-moment/image-size-limit";
import { $isDesignHuddleNodeMui } from "../nodes/design-huddle-mui";
import { $isDesignHuddleNode } from "../nodes/design-huddle-node";
import { $isImageNode } from "../nodes/image-node";
import {
  $createImageNode,
  $isImageNodeMui,
  $removePlaceholderImage,
} from "../nodes/image-node-mui";
import { $isVideoNode } from "../nodes/video-node";
import { $isVideoNodeMui } from "../nodes/video-node-mui";
import {
  DELETE_IMAGE_COMMAND,
  DRAGLEAVE_COMMAND,
  INSERT_IMAGE_COMMAND,
  INSERT_IMAGE_FILES_COMMAND,
  RESIZE_END_COMMAND,
  RESIZE_START_COMMAND,
} from "../types/commands";
import { hideTargetLine, setTargetLine } from "../utils/draggable-setters";
import { downSampleImage } from "../utils/image";
import { $insertNode } from "../utils/insert-node";
import { $isInDataTableNode, findDataTableNode } from "../utils/is-in-table-node";
import { useGetBlockElement } from "../utils/use-get-block-element";

export async function insertUrl(
  url: string,
  channel: ChannelName,
  force?: { width: number; height: number },
): Promise<{ url: string; proportionOfMaxWidth: number; aspect: number }> {
  const maxDimensions =
    channel === "slack"
      ? {
          width: 600,
          height: 400,
        }
      : {
          width: 1024,
          height: 10240,
        };
  const dimensions = force ?? (await getImageDimensions(url));
  return {
    url,
    proportionOfMaxWidth:
      getRelativeMaxDimensionsLocal(dimensions, maxDimensions).width / maxDimensions.width,
    aspect: dimensions.width / dimensions.height,
  };
}

async function uploadFile(
  file: File,
  channel: NonNullable<ChannelName>,
  token: string,
  account_id: string,
  maxSize: "small" | "large",
): Promise<{ url: string; proportionOfMaxWidth: number; aspect: number }> {
  const imageService = new ImagesService();
  let id = uuidv4() as string;
  let forceDimensions: { width: number; height: number } | undefined = undefined;

  // HACK: don't convert gifs as they are probably be animated and hopefully others are not
  let newFile = file.type === "image/gif" ? file : await downSampleImage(file, maxSize);
  if (file.type === "image/gif") id += ".gif";
  if (isEmailChannel(channel) && file.type.includes("image/svg")) {
    ({ file: newFile, dimensions: forceDimensions } = await convertSvgImage(file, channel));
  }

  await imageService.upload(newFile, id, token);

  if (file.type === "image/gif" && isChatChannel(channel)) {
    const sizeInMb = file.size / 1024 / 1024;
    const sizeLimit = imageSizeLimit(channel);
    if (sizeInMb >= sizeLimit) {
      toast.error(
        `Uploaded GIF image is too large, ensure the file size is less than ${sizeLimit}MB`,
      );
    }
  }

  return await insertUrl(`${serviceURL("images")}${account_id}/${id}`, channel, forceDimensions);
}

function onUploadError(onUploading: (uploading: boolean) => void): void {
  toast.error("Image upload failed. Please check the file format and try again.", {
    duration: 10000,
  });
  onUploading(false);
}

function handleFiles(
  editor: LexicalEditor,
  getToken: () => Promise<string>,
  onUploading: (uploading: boolean) => void,
  channel: NonNullable<ChannelName>,
  maxSize: "small" | "large",
  fileList?: FileList,
  account_id?: string,
): boolean {
  const files = Array.from(fileList || []);
  if (!account_id || files.length === 0) return false;

  void getToken().then((token) => {
    void Promise.all(files.map((file) => uploadFile(file, channel, token, account_id, maxSize)))
      .then((urls) => {
        urls.map((file) => {
          editor.dispatchCommand(INSERT_IMAGE_COMMAND, file);
        });
        onUploading(false);
      })
      .catch(() => onUploadError(onUploading));
  });

  return true;
}

type ImagePluginProps = {
  channel: NonNullable<ChannelName>;
  onDragover: (draggedOver: boolean) => void;
  onUploading: (uploading: boolean) => void;
};

const useImageCommands = ({ channel, onDragover, onUploading }: ImagePluginProps): null => {
  const [editor] = useLexicalComposerContext();
  const user = useClaims();
  const getToken = useToken();

  const maxSize = "large";

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(
          DELETE_IMAGE_COMMAND,
          (key: NodeKey) => {
            const node = $getNodeByKey(key);
            if (!$isImageNode(node)) {
              return false;
            }
            node.remove();
            return true;
          },
          COMMAND_PRIORITY_NORMAL,
        ),
        editor.registerCommand(
          RESIZE_START_COMMAND,
          ({ nodeKey }) => {
            editor.update(() => {
              const node = $getNodeByKey(nodeKey);
              if ($isImageNodeMui(node) || $isDesignHuddleNodeMui(node) || $isVideoNodeMui(node)) {
                node.setIsCurrentlyResizing(true);
              }
            });

            return true;
          },
          COMMAND_PRIORITY_HIGH,
        ),
        editor.registerCommand(
          RESIZE_END_COMMAND,
          ({ nodeKey, proportionOfMaxWidth }) => {
            editor.update(() => {
              const node = $getNodeByKey(nodeKey);
              if ($isImageNode(node) || $isDesignHuddleNode(node) || $isVideoNode(node)) {
                node.setSize(proportionOfMaxWidth, node.getAspect());
              }
              if ($isImageNodeMui(node) || $isDesignHuddleNodeMui(node) || $isVideoNodeMui(node)) {
                node.setIsCurrentlyResizing(false);
              }
            });
            return true;
          },
          COMMAND_PRIORITY_HIGH,
        ),
        editor.registerCommand(
          INSERT_IMAGE_FILES_COMMAND,
          (event) => {
            onUploading(true);

            return handleFiles(
              editor,
              getToken,
              onUploading,
              channel,
              maxSize,
              event.files,
              user?.["http://changeengine.com/account_id"],
            );
          },
          COMMAND_PRIORITY_HIGH,
        ),
        editor.registerCommand(
          PASTE_COMMAND,
          (event) => {
            if (!(event instanceof ClipboardEvent)) return false;
            if (event.clipboardData?.types.some((type) => type === "Files")) {
              onUploading(true);
              return handleFiles(
                editor,
                getToken,
                onUploading,
                channel,
                maxSize,
                event.clipboardData?.files,
                user?.["http://changeengine.com/account_id"],
              );
            }

            const data = event.clipboardData?.getData("text/plain");
            if (data?.match(/^<img[^>]*\/>$/)) {
              const d = new DOMParser().parseFromString(data, "text/html");
              if (d.body.firstElementChild?.tagName === "IMG") {
                const src = d.body.firstElementChild?.getAttribute("src");
                if (!src) return false;
                insertUrl(src, channel)
                  .then((file) => editor.dispatchCommand(INSERT_IMAGE_COMMAND, file))
                  .catch(() => onUploadError(onUploading));
                return true;
              }
            }

            return false;
          },
          COMMAND_PRIORITY_NORMAL,
        ),
      ),
    [editor, getToken, user, onUploading, channel, maxSize],
  );

  useEffect(
    () =>
      editor.registerCommand(
        INSERT_IMAGE_COMMAND,
        ({ url, proportionOfMaxWidth, aspect, sourceAssetId }) => {
          $removePlaceholderImage();

          const imageNode = $createImageNode(
            editor._headless,
            url,
            proportionOfMaxWidth,
            aspect,
            undefined,
            sourceAssetId,
          );

          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const node = selection.anchor.getNode();

            if ($isInDataTableNode(node)) {
              const dataTableNode = findDataTableNode(node);
              dataTableNode?.insertAfter(imageNode);
              return true;
            }
          }

          $insertNode(imageNode);
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    [editor, getToken, user, onUploading, channel, onDragover],
  );

  return null;
};

export const ImagePlugin: FC<
  ImagePluginProps & {
    anchorElem?: HTMLElement;
  }
> = (props): React.JSX.Element => {
  const { onDragover, onUploading, channel, anchorElem } = props;
  const [editor] = useLexicalComposerContext();
  const user = useClaims();
  const getToken = useToken();
  const maxSize = "large";

  const targetLineRef = useRef<HTMLDivElement>(null);
  const getBlockElement = useGetBlockElement();

  const anchorElement = anchorElem ?? document.body;

  useImageCommands(props);

  useEffect(
    () =>
      editor.registerCommand(
        DRAGLEAVE_COMMAND,
        () => {
          onDragover(false);
          targetLineRef.current && hideTargetLine(targetLineRef.current);
          return false;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    [editor, onDragover],
  );

  useEffect(
    () =>
      editor.registerCommand(
        DRAGOVER_COMMAND,
        (event) => {
          event.preventDefault();

          if ((event.dataTransfer?.items ?? []).length === 0) {
            onDragover(false);
            targetLineRef.current && hideTargetLine(targetLineRef.current);
            return false;
          }

          const transferItems = Array.from(event.dataTransfer?.items ?? []).filter(
            ({ type }) => type !== "application/x-lexical-drag-block",
          );

          const targetBlockElem = getBlockElement(anchorElement, editor, event);

          if (
            event.dataTransfer &&
            transferItems.length !== 0 &&
            targetBlockElem &&
            targetLineRef.current
          ) {
            event.dataTransfer.dropEffect = "copy";
            onDragover(true);
            setTargetLine(targetLineRef.current, targetBlockElem, event.clientY, anchorElement);
          } else {
            onDragover(false);
            targetLineRef.current && hideTargetLine(targetLineRef.current);
          }

          return true;
        },

        COMMAND_PRIORITY_LOW,
      ),
    [editor, onDragover, anchorElement, getBlockElement],
  );
  useEffect(
    () =>
      editor.registerCommand(
        DROP_COMMAND,
        (event) => {
          event.preventDefault();

          const transferItems = Array.from(event.dataTransfer?.items ?? []).filter(
            ({ type }) => type !== "application/x-lexical-drag-block",
          );

          if (transferItems.length === 0) {
            return false;
          }

          // DNDed HTML images
          transferItems
            .filter(({ type }) => type === "text/html")
            .forEach((item) => {
              onUploading(true);

              item.getAsString((data) => {
                const d = new DOMParser()
                  .parseFromString(data, "text/html")
                  .getElementsByTagName("img");
                if (d.length > 0) {
                  const src = d[0]!.getAttribute("src");
                  if (src)
                    insertUrl(src, channel)
                      .then((file) => editor.dispatchCommand(INSERT_IMAGE_COMMAND, file))
                      .catch(() => onUploadError(onUploading));
                  onUploading(false);
                }
              });
            });

          onDragover(false);

          // DNDed upload images
          if (event.dataTransfer?.files.length !== 0) {
            onUploading(true);
          }

          const selectedHtmlNode = getBlockElement(anchorElement, editor, event);

          if (selectedHtmlNode) {
            const selectedNode = $getNearestNodeFromDOMNode(selectedHtmlNode);
            if (selectedNode) {
              const nodeSelection = $createNodeSelection();

              nodeSelection.add(selectedNode.getKey());
              $setSelection(nodeSelection);
            }
          }

          targetLineRef.current && hideTargetLine(targetLineRef.current);

          return handleFiles(
            editor,
            getToken,
            onUploading,
            channel,
            maxSize,
            event.dataTransfer?.files,
            user?.["http://changeengine.com/account_id"],
          );
        },
        COMMAND_PRIORITY_HIGH,
      ),
    [
      editor,
      onDragover,
      anchorElement,
      getBlockElement,
      getToken,
      user,
      onUploading,
      channel,
      maxSize,
    ],
  );

  return createPortal(
    <>
      <div className="draggable-block-target-line" ref={targetLineRef} />
    </>,
    anchorElement,
  );
};
